// tradeshowTheme.js
// Modern blue/gray theme inspired by provided print screen. No orange hues.

import { createTheme } from '@mui/material/styles';

const tradeshowTheme = createTheme({
  palette: {
    primary: {
      main: '#0A66C2', // strong blue for headings/buttons
      light: '#4C8ED8',
      dark: '#084C8F',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0056B3',
      light: '#4C8ED8',
      dark: '#003E80',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F3F5F7', // soft light gray page background
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2937', // slate-800
      secondary: '#4B5563', // slate-600
    },
    success: {
      main: '#1B9E77',
      light: '#54BE9E',
      dark: '#157A5C',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#B58900', // muted amber (no orange)
      light: '#DCB44E',
      dark: '#8A6A00',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D14343',
      light: '#E57373',
      dark: '#A33232',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2563EB',
      light: '#60A5FA',
      dark: '#1E40AF',
      contrastText: '#FFFFFF',
    },
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif',
    h1: { fontWeight: 800, color: '#0A66C2', letterSpacing: 0.2 },
    h2: { fontWeight: 700, color: '#0A66C2' },
    h3: { fontWeight: 600, color: '#0A66C2' },
    h4: { fontWeight: 600, color: '#1F2937' },
    h5: { fontWeight: 600, color: '#1F2937' },
    h6: { fontWeight: 600, color: '#1F2937' },
    subtitle1: { fontWeight: 500, color: '#1F2937' },
    subtitle2: { fontWeight: 500, color: '#4B5563' },
    body1: { lineHeight: 1.7, color: '#1F2937' },
    body2: { lineHeight: 1.6, color: '#4B5563' },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(17, 24, 39, 0.08)', // soft shadow
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 6px 20px rgba(17, 24, 39, 0.08)',
          border: '1px solid #E5E7EB',
          backgroundImage:
            'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(249,250,251,1) 100%)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          boxShadow: 'none',
        },
        contained: {
          background:
            'linear-gradient(135deg, #0A66C2 0%, #2563EB 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background:
              'linear-gradient(135deg, #084C8F 0%, #1E40AF 100%)',
            boxShadow: '0 6px 16px rgba(37, 99, 235, 0.35)',
          },
        },
        outlined: {
          borderWidth: 2,
          borderColor: '#0A66C2',
          color: '#0A66C2',
          '&:hover': {
            borderColor: '#084C8F',
            backgroundColor: 'rgba(10, 102, 194, 0.06)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2563EB',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0A66C2',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#0A66C2',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: { backgroundColor: '#E8F5EF', color: '#157A5C' },
        standardError: { backgroundColor: '#FDECEC', color: '#A33232' },
        standardWarning: { backgroundColor: '#FFF7E6', color: '#8A6A00' },
        standardInfo: { backgroundColor: '#EAF2FD', color: '#1E40AF' },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-root .Mui-completed': { color: '#1B9E77' },
          '& .MuiStepLabel-root .Mui-active': { color: '#0A66C2' },
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: '#E5E7EB' } } },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#0A66C2',
          '&.Mui-checked': { color: '#0A66C2' },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          '&.orange-header-box': {
            // ensure no orange header is used; provide blue gradient variant
            background: 'linear-gradient(135deg, #0A66C2 0%, #2563EB 100%)',
            color: '#FFFFFF',
          },
        },
      },
    },
  },
});

export default tradeshowTheme;
